import React, { useEffect, useState } from 'react';
import { useAuth } from '../lib/AuthContext';
import { useOutlet } from '../lib/OutletContext';
import { getTransfers, approveTransfer, requestTransfer, getIngredients, getOutlets } from '../lib/api';
import { ArrowLeftRight, CheckCircle, Plus } from 'lucide-react';
import { formatNumber, formatDateTime } from '../lib/utils';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import logger from '../lib/logger';

const Transfers = () => {
  const { user } = useAuth();
  const { selectedOutlet } = useOutlet();
  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // New Transfer Modal State
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [ingredients, setIngredients] = useState([]);
  const [outlets, setOutlets] = useState([]);
  const [newTransfer, setNewTransfer] = useState({
    to_outlet_id: '',
    ingredient_id: '',
    quantity: '',
    base_unit: 'kg'
  });

  useEffect(() => {
    loadTransfers();
    loadIngredients();
    loadOutlets();
  }, [selectedOutlet]);

  const loadTransfers = async () => {
    try {
      const outletId = selectedOutlet;
      const data = await getTransfers(outletId);
      setTransfers(data);
    } catch (error) {
      toast.error('Failed to load transfers');
      logger.error(error);
    } finally {
      setLoading(false);
    }
  };

  const loadIngredients = async () => {
    try {
      const data = await getIngredients(selectedOutlet);
      setIngredients(data.ingredients || []);
    } catch (error) {
      logger.error('Failed to load ingredients:', error);
    }
  };

  const loadOutlets = async () => {
    try {
      const data = await getOutlets();
      setOutlets(data || []);
    } catch (error) {
      logger.error('Failed to load outlets:', error);
    }
  };

  const handleApprove = async (transferId) => {
    try {
      await approveTransfer(transferId);
      toast.success('Transfer approved and completed');
      loadTransfers();
    } catch (error) {
      toast.error('Failed to approve transfer');
      logger.error(error);
    }
  };

  const handleCreateTransfer = async (e) => {
    e.preventDefault();
    
    // Validate
    if (!newTransfer.to_outlet_id) {
      toast.error('Destination outlet is required');
      return;
    }
    if (newTransfer.to_outlet_id === selectedOutlet) {
      toast.error('Cannot transfer to the same outlet');
      return;
    }
    if (!newTransfer.ingredient_id) {
      toast.error('Ingredient is required');
      return;
    }
    if (!newTransfer.quantity || parseFloat(newTransfer.quantity) <= 0) {
      toast.error('Quantity must be greater than 0');
      return;
    }

    const selectedIngredient = ingredients.find(i => i.ingredient_id === newTransfer.ingredient_id);

    setCreating(true);
    try {
      const fromOutlet = outlets.find(o => o.outlet_id === selectedOutlet);
      const toOutlet = outlets.find(o => o.outlet_id === newTransfer.to_outlet_id);
      
      await requestTransfer({
        from_outlet_id: selectedOutlet,
        from_outlet_name: fromOutlet?.outlet_name || null,
        to_outlet_id: newTransfer.to_outlet_id,
        to_outlet_name: toOutlet?.outlet_name || null,
        ingredient_id: newTransfer.ingredient_id,
        ingredient_name: selectedIngredient?.ingredient_name || '',
        quantity: parseFloat(newTransfer.quantity),
        base_unit: newTransfer.base_unit
      });
      toast.success('Transfer request created');
      setCreateDialogOpen(false);
      resetCreateForm();
      loadTransfers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create transfer');
      logger.error(error);
    } finally {
      setCreating(false);
    }
  };

  const resetCreateForm = () => {
    setNewTransfer({
      to_outlet_id: '',
      ingredient_id: '',
      quantity: '',
      base_unit: 'kg'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'PENDING':
        return 'bg-amber-500/10 text-amber-500 border-amber-500/20';
      case 'APPROVED':
      case 'DISPATCHED':
        return 'bg-blue-500/10 text-blue-500 border-blue-500/20';
      case 'RECEIVED':
        return 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20';
      case 'CANCELLED':
        return 'bg-red-500/10 text-red-500 border-red-500/20';
      default:
        return 'bg-zinc-500/10 text-zinc-500 border-zinc-500/20';
    }
  };

  // Get current stock for selected ingredient
  const selectedIngredientStock = newTransfer.ingredient_id
    ? ingredients.find(i => i.ingredient_id === newTransfer.ingredient_id)?.soh || 0
    : 0;

  // Count pending transfers that need approval (where current outlet is source)
  const pendingCount = transfers.filter(t => t.status === 'PENDING' && t.from_outlet_id === selectedOutlet).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="loading-spinner">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="transfers-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-black tracking-tight mb-2" data-testid="transfers-title">
            Transfers
            {pendingCount > 0 && (
              <span className="ml-3 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-amber-500/10 text-amber-500 border border-amber-500/20">
                {pendingCount} pending
              </span>
            )}
          </h1>
          <p className="text-zinc-400">Inter-outlet inventory transfers</p>
        </div>
        
        {/* New Transfer Button */}
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogTrigger asChild>
            <button
              className="flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white font-bold py-2 px-4 rounded-lg transition-colors"
              data-testid="new-transfer-button"
            >
              <Plus className="w-5 h-5" />
              New Transfer
            </button>
          </DialogTrigger>
          <DialogContent className="bg-[#18181b] border-[#27272a] text-white max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold">Create Transfer Request</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateTransfer} className="space-y-4 mt-4">
              {/* From Outlet (Current - Read Only) */}
              <div>
                <label className="block text-sm font-medium mb-2">From Outlet</label>
                <input
                  type="text"
                  value={outlets.find(o => o.outlet_id === selectedOutlet)?.outlet_name || selectedOutlet}
                  disabled
                  className="w-full px-4 py-2 bg-[#09090b] border border-[#3f3f46] rounded-md text-zinc-400"
                />
              </div>

              {/* To Outlet */}
              <div>
                <label className="block text-sm font-medium mb-2">To Outlet *</label>
                <select
                  value={newTransfer.to_outlet_id}
                  onChange={(e) => setNewTransfer(prev => ({ ...prev, to_outlet_id: e.target.value }))}
                  className="w-full px-4 py-2 bg-[#09090b] border border-[#3f3f46] rounded-md focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none"
                  required
                  data-testid="to-outlet-select"
                >
                  <option value="">Select destination outlet...</option>
                  {outlets
                    .filter(o => o.outlet_id !== selectedOutlet)
                    .map((outlet) => (
                      <option key={outlet.outlet_id} value={outlet.outlet_id}>
                        {outlet.outlet_name}
                      </option>
                    ))}
                </select>
              </div>

              {/* Ingredient */}
              <div>
                <label className="block text-sm font-medium mb-2">Ingredient *</label>
                <select
                  value={newTransfer.ingredient_id}
                  onChange={(e) => {
                    const ing = ingredients.find(i => i.ingredient_id === e.target.value);
                    setNewTransfer(prev => ({ 
                      ...prev, 
                      ingredient_id: e.target.value,
                      base_unit: ing?.base_unit || 'kg'
                    }));
                  }}
                  className="w-full px-4 py-2 bg-[#09090b] border border-[#3f3f46] rounded-md focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none"
                  required
                  data-testid="ingredient-select"
                >
                  <option value="">Select ingredient...</option>
                  {ingredients.map((ing) => (
                    <option key={ing.ingredient_id} value={ing.ingredient_id}>
                      {ing.ingredient_name} (Stock: {formatNumber(ing.soh || 0)} {ing.base_unit})
                    </option>
                  ))}
                </select>
                {newTransfer.ingredient_id && (
                  <p className="text-xs text-zinc-400 mt-1">
                    Available stock: <span className="font-mono text-emerald-500">{formatNumber(selectedIngredientStock)}</span>
                  </p>
                )}
              </div>

              {/* Quantity */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Quantity *</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={newTransfer.quantity}
                    onChange={(e) => setNewTransfer(prev => ({ ...prev, quantity: e.target.value }))}
                    className="w-full px-4 py-2 bg-[#09090b] border border-[#3f3f46] rounded-md focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none"
                    placeholder="Enter quantity"
                    required
                    data-testid="quantity-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Unit</label>
                  <select
                    value={newTransfer.base_unit}
                    onChange={(e) => setNewTransfer(prev => ({ ...prev, base_unit: e.target.value }))}
                    className="w-full px-4 py-2 bg-[#09090b] border border-[#3f3f46] rounded-md focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none"
                    data-testid="unit-select"
                  >
                    <option value="kg">kg</option>
                    <option value="gm">gm</option>
                    <option value="l">l</option>
                    <option value="ml">ml</option>
                    <option value="pcs">pcs</option>
                  </select>
                </div>
              </div>

              {/* Submit Button */}
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => { setCreateDialogOpen(false); resetCreateForm(); }}
                  className="flex-1 bg-zinc-700 hover:bg-zinc-600 text-white font-bold py-2 px-4 rounded-md transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creating}
                  className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white font-bold py-2 px-4 rounded-md transition-colors disabled:opacity-50"
                  data-testid="submit-transfer-button"
                >
                  {creating ? 'Creating...' : 'Request Transfer'}
                </button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Transfers List */}
      <div className="space-y-4" data-testid="transfers-list">
        {transfers.length === 0 ? (
          <div className="text-center py-12 bg-[#18181b] border border-[#27272a] rounded-lg">
            <ArrowLeftRight className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
            <p className="text-zinc-500 mb-4">No transfers found</p>
            <button
              onClick={() => setCreateDialogOpen(true)}
              className="text-emerald-500 hover:text-emerald-400 text-sm font-medium"
            >
              Create your first transfer
            </button>
          </div>
        ) : (
          transfers.map((transfer) => (
            <div
              key={transfer.transfer_id}
              className="bg-[#18181b] border border-[#27272a] rounded-lg p-6"
              data-testid="transfer-card"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                      <ArrowLeftRight className="w-5 h-5 text-emerald-500" />
                    </div>
                    <div>
                      <h3 className="font-bold" data-testid="transfer-ingredient">{transfer.ingredient_name}</h3>
                      <p className="text-sm text-zinc-400">
                        {transfer.from_outlet_name || outlets.find(o => o.outlet_id === transfer.from_outlet_id)?.outlet_name || 'Unknown'} 
                        {' → '}
                        {transfer.to_outlet_name || outlets.find(o => o.outlet_id === transfer.to_outlet_id)?.outlet_name || 'Unknown'}
                      </p>
                    </div>
                  </div>
                </div>
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(transfer.status)}`}>
                  {transfer.status}
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div className="p-3 bg-[#09090b] border border-[#27272a] rounded-lg">
                  <p className="text-xs text-zinc-400 mb-1">Quantity</p>
                  <p className="text-lg font-mono font-bold" data-testid="transfer-quantity">
                    {formatNumber(transfer.quantity)} {transfer.base_unit}
                  </p>
                </div>
                <div className="p-3 bg-[#09090b] border border-[#27272a] rounded-lg">
                  <p className="text-xs text-zinc-400 mb-1">Requested By</p>
                  <p className="text-sm font-medium">{transfer.requested_by_email || transfer.requested_by?.slice(0, 12) || 'Unknown'}</p>
                </div>
                <div className="p-3 bg-[#09090b] border border-[#27272a] rounded-lg">
                  <p className="text-xs text-zinc-400 mb-1">Requested At</p>
                  <p className="text-sm">{formatDateTime(transfer.requested_at)}</p>
                </div>
              </div>

              {transfer.status === 'PENDING' && transfer.from_outlet_id === selectedOutlet && (
                <button
                  onClick={() => handleApprove(transfer.transfer_id)}
                  className="flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white font-bold py-2 px-4 rounded-md transition-colors"
                  data-testid="approve-transfer-button"
                >
                  <CheckCircle className="w-4 h-4" />
                  Approve & Complete Transfer
                </button>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default Transfers;
